import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { getHHMM } from 'utils/date';
import * as Styles from './styles';
import Image from 'next/image';
import { getImageBasePath } from 'utils/generics';
import { useEffect, useState } from 'react';

enum TabOptions {
    PENDING = 'Pending',
    COMPLETED = 'Completed',
}

interface IProps {
    selectedTab: TabOptions;
    variantName: string;
    lastUpdatedAt: string;
    status: string;
    statusMessage: string;
    planName: string;
    mobileNumber: string;
    amount: number;
    onClick: any;
    isBtnLoading: boolean;
    vin?: string;
    customerName: string;
    redirectUrl?: string | null;
}

function TransactionDetailsModal({
    selectedTab,
    variantName,
    lastUpdatedAt,
    status,
    statusMessage,
    planName,
    mobileNumber,
    amount,
    onClick,
    isBtnLoading,
    vin,
    customerName,
    redirectUrl,
}: IProps) {
    const [linkCopied, setLinkCopied] = useState(false);

    useEffect(() => {
        let timer;
        if (linkCopied) {
            timer = setTimeout(() => {
                setLinkCopied(false);
            }, 1200);
        }

        return () => {
            timer && clearTimeout(timer);
        };
    }, [linkCopied]);

    const getColors = () => {
        switch (statusMessage) {
            case 'Expired':
                return {
                    color: '#D83D37',
                    background: '#FBEAEA',
                };
            case 'Payment Done, KYC Pending':
                return {
                    color: '#F58700',
                    background: '#FFF7E5',
                };
            case 'Payment Pending':
                return {
                    color: '#5920C5',
                    background: '#EFE9FB',
                };
            default:
                return {
                    color: '#0FA457',
                    background: '#EBFBEE',
                };
        }
    };

    const { background, color } = getColors();

    const copyLink = (link: string) => {
        navigator.clipboard.writeText(link);
    };

    return (
        <Styles.Wrapper>
            <Styles.ModalName>{customerName}</Styles.ModalName>
            <Styles.LastUpdatedDate>
                Last updated {getHHMM(lastUpdatedAt)}
            </Styles.LastUpdatedDate>
            <Styles.StatusText background={background} color={color}>
                {statusMessage || status}
            </Styles.StatusText>
            <Styles.PlanDetailsWrapper>
                <Styles.RowWrapper>
                    <Styles.LabelText>Vehicle model</Styles.LabelText>
                    <Styles.ValueText>{variantName}</Styles.ValueText>
                </Styles.RowWrapper>
                <Styles.Hr />
                <>
                    <Styles.RowWrapper>
                        <Styles.LabelText>Chassis number</Styles.LabelText>
                        <Styles.ValueText>{vin}</Styles.ValueText>
                    </Styles.RowWrapper>
                    <Styles.Hr />
                    <Styles.RowWrapper>
                        <Styles.LabelText>Plan Name</Styles.LabelText>
                        <Styles.ValueText>{planName}</Styles.ValueText>
                    </Styles.RowWrapper>
                </>
            </Styles.PlanDetailsWrapper>
            <Styles.CustomerDetailsWrapper>
                <Styles.CustomerMobileFlex>
                    <Styles.CustomerMobileText>
                        Customer mobile number
                    </Styles.CustomerMobileText>
                    <Styles.CustomerMobileNumber>
                        {mobileNumber}
                    </Styles.CustomerMobileNumber>
                </Styles.CustomerMobileFlex>
                <Styles.CustomerAmountFlex>
                    <Styles.CustomerMobileFlex>
                        <Styles.CustomerMobileText>
                            Amount
                        </Styles.CustomerMobileText>
                        <Styles.CustomerMobileNumber>
                            ₹{amount}
                        </Styles.CustomerMobileNumber>
                    </Styles.CustomerMobileFlex>
                </Styles.CustomerAmountFlex>
            </Styles.CustomerDetailsWrapper>
            {redirectUrl && (
                <>
                    <Styles.LinkWrapper>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                gap: '1rem',
                                width: 'max-content',
                                position: 'relative',
                            }}
                        >
                            <Styles.RedirectionLinkText>
                                Customer link
                            </Styles.RedirectionLinkText>
                            {linkCopied && (
                                <Styles.Copied>Link copied</Styles.Copied>
                            )}
                        </div>
                        <Styles.LinkContainer>
                            <Styles.LinkText>{redirectUrl}</Styles.LinkText>
                            <Image
                                width={'16px'}
                                height={'16px'}
                                alt={''}
                                src={getImageBasePath('/icons/copy.svg')}
                                onClick={() => {
                                    copyLink(redirectUrl);
                                    setLinkCopied(true);
                                }}
                            />
                        </Styles.LinkContainer>
                    </Styles.LinkWrapper>
                </>
            )}
            {selectedTab === TabOptions.PENDING &&
                (statusMessage === 'Payment Pending' ||
                    statusMessage === 'Payment Done, KYC Pending') && (
                    <Styles.ResendLinkWrapper mt={'32px'}>
                        <PrimaryButton
                            loading={isBtnLoading}
                            isFullWidth={true}
                            size={'medium'}
                            onClick={() => onClick()}
                        >
                            Resend link
                        </PrimaryButton>
                    </Styles.ResendLinkWrapper>
                )}
        </Styles.Wrapper>
    );
}

export default TransactionDetailsModal;
